// Funções para extrair geometrias do `ResultResponse` do RouteCore e
// converter para o formato que o react-leaflet consome.
//
// O RouteCore expõe DUAS formas paralelas de geometria:
//
//  1. Por rota — `route.geojson_line` (LineString) ou `route.polyline_encoded`
//     (Google encoded). Hoje, o solver Python NÃO preenche esses campos no
//     payload de resposta; mantemos o suporte para versões futuras.
//
//  2. Agregada — `map_outputs.geojson` (FeatureCollection com 1 Point por
//     parada, agrupado por `route_id` em properties) e `map_outputs.polylines`
//     (array de strings no formato customizado `"lat,lon|lat,lon|..."`, uma
//     por rota, na mesma ordem de `routes[]`).
//
// Este módulo tenta (2) primeiro porque é o que o solver atual produz, e cai
// para (1) quando estiver disponível.

import type { LatLng, Route, ResultResponse } from "../types/routecore";

export type RouteLine = {
  routeId: string;
  driver: string;
  vehicle: string;
  positions: [number, number][];
  stops: {
    sequence: number;
    type: string;
    orderId?: string;
    position: [number, number];
    eta: string;
  }[];
};

// ---------------------------------------------------------------------------
// Decodificadores de geometria
// ---------------------------------------------------------------------------

// Decodifica polylines no formato Google Encoded Polyline (precision
// 5). Implementação simples; usada apenas quando o resultado vier no
// formato canônico.
function decodeGooglePolyline(str: string, precision = 5): [number, number][] {
  let index = 0;
  let lat = 0;
  let lng = 0;
  const coords: [number, number][] = [];
  const factor = Math.pow(10, precision);
  while (index < str.length) {
    let b: number;
    let shift = 0;
    let result = 0;
    do {
      b = str.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lat += dlat;
    shift = 0;
    result = 0;
    do {
      b = str.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lng += dlng;
    coords.push([lat / factor, lng / factor]);
  }
  return coords;
}

// Decodifica polyline no formato customizado do RouteCore:
//   "lat,lon|lat,lon|lat,lon"
// Tolerante a espaços e a separadores `|` ou `;`.
function decodeCustomPolyline(str: string): [number, number][] {
  if (!str) return [];
  const out: [number, number][] = [];
  for (const segment of str.split(/[|;]/)) {
    const parts = segment.trim().split(",");
    if (parts.length < 2) continue;
    const lat = Number(parts[0]);
    const lon = Number(parts[1]);
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      out.push([lat, lon]);
    }
  }
  return out;
}

// Tenta os dois formatos: custom primeiro (RouteCore atual), Google depois.
function decodePolylineSmart(str: string): [number, number][] {
  if (!str) return [];
  if (str.includes(",")) return decodeCustomPolyline(str);
  try {
    return decodeGooglePolyline(str);
  } catch {
    return [];
  }
}

function fromGeoJSONLine(geo: unknown): [number, number][] | null {
  if (!geo || typeof geo !== "object") return null;
  const g = geo as { type?: string; coordinates?: unknown };
  if (g.type !== "LineString" || !Array.isArray(g.coordinates)) return null;
  const out: [number, number][] = [];
  for (const c of g.coordinates as unknown[]) {
    if (Array.isArray(c) && c.length >= 2) {
      const [lon, lat] = c as number[];
      if (typeof lat === "number" && typeof lon === "number") {
        out.push([lat, lon]);
      }
    }
  }
  return out.length ? out : null;
}

// ---------------------------------------------------------------------------
// Extração de paradas via map_outputs.geojson
//
// As features são Points; agrupamos por `properties.route_id` e ordenamos
// por `properties.sequence` para reconstruir a sequência de paradas com
// coordenadas válidas.
// ---------------------------------------------------------------------------

type StopFeatureProps = {
  route_id?: string;
  sequence?: number;
  kind?: string;
  order_external_id?: string | null;
  depot_external_id?: string | null;
  arrival_at?: string;
};

type StopFromGeoJSON = {
  routeId: string;
  sequence: number;
  type: string;
  orderId?: string;
  position: [number, number];
  eta: string;
};

function isPointFeature(f: unknown): f is {
  type: "Feature";
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: StopFeatureProps;
} {
  if (!f || typeof f !== "object") return false;
  const x = f as { geometry?: { type?: string; coordinates?: unknown } };
  return x.geometry?.type === "Point" && Array.isArray(x.geometry.coordinates);
}

function extractStopsByRoute(
  geojson: unknown,
): Map<string, StopFromGeoJSON[]> {
  const map = new Map<string, StopFromGeoJSON[]>();
  if (!geojson || typeof geojson !== "object") return map;
  const fc = geojson as { type?: string; features?: unknown[] };
  if (fc.type !== "FeatureCollection" || !Array.isArray(fc.features)) {
    return map;
  }
  for (const f of fc.features) {
    if (!isPointFeature(f)) continue;
    const [lon, lat] = f.geometry.coordinates as [number, number];
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    const props = f.properties || {};
    const routeId = props.route_id ?? "_unknown";
    const entry: StopFromGeoJSON = {
      routeId,
      sequence: typeof props.sequence === "number" ? props.sequence : 0,
      type: props.kind ?? "stop",
      orderId: props.order_external_id ?? undefined,
      position: [lat, lon],
      eta: props.arrival_at ?? "",
    };
    if (!map.has(routeId)) map.set(routeId, []);
    map.get(routeId)!.push(entry);
  }
  // ordena cada rota por sequence
  for (const arr of map.values()) {
    arr.sort((a, b) => a.sequence - b.sequence);
  }
  return map;
}

// ---------------------------------------------------------------------------
// Conversão Route → RouteLine
// ---------------------------------------------------------------------------

// Ordem de preferência para a geometria da linha (positions):
//
//   1. route.geojson_line          (LineString — formato canônico)
//   2. route.polyline_encoded      (Google encoded)
//   3. map_outputs.polylines[i]    (custom "lat,lon|...")
//   4. ligação reta entre as paradas (vindas do geojson agregado)
//   5. ligação reta entre route.stops (último recurso; lat/lon podem ser 0)
function buildPositions(
  route: Route,
  fallbackStops: [number, number][] | undefined,
  aggregatePolyline: string | undefined,
): [number, number][] {
  const fromLine = fromGeoJSONLine(route.geojson_line);
  if (fromLine && fromLine.length) return fromLine;

  if (route.polyline_encoded) {
    const decoded = decodePolylineSmart(route.polyline_encoded);
    if (decoded.length) return decoded;
  }

  if (aggregatePolyline) {
    const decoded = decodePolylineSmart(aggregatePolyline);
    if (decoded.length) return decoded;
  }

  if (fallbackStops && fallbackStops.length) return fallbackStops;

  // Último recurso — pega das stops embutidas (provavelmente com 0,0)
  return route.stops
    .filter(
      (s) =>
        s.location &&
        Number.isFinite(s.location.lat) &&
        Number.isFinite(s.location.lon) &&
        !(s.location.lat === 0 && s.location.lon === 0),
    )
    .map((s) => [s.location.lat, s.location.lon] as [number, number]);
}

function buildStops(
  route: Route,
  fromGeoJSON: StopFromGeoJSON[] | undefined,
): RouteLine["stops"] {
  // Preferimos as paradas vindas do geojson (têm lat/lon reais)
  if (fromGeoJSON && fromGeoJSON.length) {
    return fromGeoJSON.map((s) => ({
      sequence: s.sequence,
      type: s.type,
      orderId: s.orderId,
      position: s.position,
      eta: s.eta,
    }));
  }
  // Fallback: usa route.stops (pode estar com lat=0/lon=0)
  return route.stops.map((s) => ({
    sequence: s.sequence,
    type: s.type,
    orderId: s.order_id,
    position: [s.location.lat, s.location.lon] as [number, number],
    eta: s.eta,
  }));
}

export function resultToRouteLines(
  result: ResultResponse | undefined | null,
): RouteLine[] {
  if (!result) return [];
  const stopsByRoute = extractStopsByRoute(result.map_outputs?.geojson);
  const polylines = result.map_outputs?.polylines ?? [];

  return result.routes.map((route, i) => {
    const geoStops = stopsByRoute.get(route.route_id);
    const stops = buildStops(route, geoStops);
    const stopPositions = geoStops?.map((s) => s.position);
    const positions = buildPositions(route, stopPositions, polylines[i]);
    return {
      routeId: route.route_id,
      driver: route.driver_external_id,
      vehicle: route.vehicle_external_id,
      positions,
      stops,
    };
  });
}

export function bboxFromLines(lines: RouteLine[]): [LatLng, LatLng] | null {
  let minLat = Infinity;
  let minLon = Infinity;
  let maxLat = -Infinity;
  let maxLon = -Infinity;
  for (const l of lines) {
    for (const [lat, lon] of l.positions) {
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lon < minLon) minLon = lon;
      if (lon > maxLon) maxLon = lon;
    }
    // Considera também as stops, que podem extrapolar a polyline.
    for (const s of l.stops) {
      const [lat, lon] = s.position;
      if (lat === 0 && lon === 0) continue;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lon < minLon) minLon = lon;
      if (lon > maxLon) maxLon = lon;
    }
  }
  if (!isFinite(minLat) || !isFinite(maxLat)) return null;
  return [
    { lat: minLat, lon: minLon },
    { lat: maxLat, lon: maxLon },
  ];
}

// Mantido para retrocompatibilidade com qualquer import externo.
export function toRouteLine(route: Route): RouteLine {
  return resultToRouteLines({
    routes: [route],
    map_outputs: {},
  } as unknown as ResultResponse)[0];
}
